import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { navLinks } from '../data/mock';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    
    if (link.isRoute) {
      // Navigate to a different page
      navigate(link.href);
    } else {
      // Handle anchor links
      if (location.pathname !== '/') {
        // If not on homepage, navigate to homepage first then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(link.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Already on homepage, just scroll
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={handleLogoClick}>
            <img 
              src="https://customer-assets.emergentagent.com/job_roseville-dental/artifacts/8rcxrz7o_Sunshine-Dental-Care-ver2-white-bg.jpg" 
              alt="Sunshine Dental Care Logo"
              className="h-28 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className={`font-medium transition-colors hover:text-cyan-500 ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {link.name}
              </button>
            ))}
          </div>

          {/* Phone Button */}
          <a
            href="tel:9167731122"
            className="hidden md:flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-5 py-2.5 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Phone size={18} />
            <span className="font-medium">(916) 773-1122</span>
          </a>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? 'text-gray-700' : 'text-white'} size={24} />
            ) : (
              <Menu className={isScrolled ? 'text-gray-700' : 'text-white'} size={24} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-cyan-600"
              >
                {link.name}
              </button>
            ))}
            <a
              href="tel:9167731122"
              className="flex items-center gap-2 mx-4 mt-4 bg-cyan-600 text-white px-5 py-3 rounded-full justify-center"
            >
              <Phone size={18} />
              <span className="font-medium">(916) 773-1122</span>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
